// Hook for getting current market prices from multiple sources
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePricePolling } from './usePricePolling';
import type { Ticker } from '../types/mexc';

export interface CurrentPriceData {
  price: number;
  source: 'polling' | 'api' | 'fallback';
  timestamp: number;
  ticker?: Ticker;
}

export interface UseCurrentPricesReturn {
  getCurrentPrice: (symbol: string) => CurrentPriceData | null;
  getAllPrices: () => Record<string, CurrentPriceData>;
  isPollingActive: boolean;
  lastUpdate: number;
}

export function useCurrentPrices(): UseCurrentPricesReturn {
  const queryClient = useQueryClient();
  const { tickers: polledTickers, lastPollTime, isPolling } = usePricePolling();

  // Get current price for a specific symbol
  const getCurrentPrice = useCallback((symbol: string): CurrentPriceData | null => {
    // Priority 1: Polled real-time data
    const ticker = polledTickers[symbol];
    if (ticker && ticker.lastPrice && ticker.lastPrice > 0) {
      return {
        price: ticker.lastPrice,
        source: 'polling',
        timestamp: ticker.timestamp || lastPollTime,
        ticker,
      };
    }

    // Priority 2: React Query cached ticker data (from API)
    const cachedTicker = queryClient.getQueryData(['ticker', symbol]) as Ticker;
    if (cachedTicker && cachedTicker.lastPrice && cachedTicker.lastPrice > 0) {
      return {
        price: cachedTicker.lastPrice,
        source: 'api',
        timestamp: cachedTicker.timestamp || Date.now(),
        ticker: cachedTicker,
      };
    }

    // Priority 3: Check all tickers cache
    const allTickers = queryClient.getQueryData(['ticker']) as Ticker[] | Ticker;
    if (allTickers) {
      let foundTicker: Ticker | null = null;

      if (Array.isArray(allTickers)) {
        foundTicker = allTickers.find(t => t.symbol === symbol) || null;
      } else if (allTickers.symbol === symbol) {
        foundTicker = allTickers;
      }

      if (foundTicker && foundTicker.lastPrice && foundTicker.lastPrice > 0) {
        return {
          price: foundTicker.lastPrice,
          source: 'api',
          timestamp: foundTicker.timestamp || Date.now(),
          ticker: foundTicker,
        };
      }
    }

    // Only log warning if we've been polling for a while and still no data
    const hasPolledData = Object.keys(polledTickers).length > 0 || queryClient?.getQueryData(['ticker']);
    if (hasPolledData) {
      console.warn(`⚠️ No current price available for ${symbol} (Polling: ${isPolling ? 'active' : 'idle'}, Cache: ${queryClient ? 'available' : 'unavailable'})`);
    }

    return null;
  }, [polledTickers, lastPollTime, isPolling, queryClient]);

  // Get all available current prices
  const getAllPrices = useCallback((): Record<string, CurrentPriceData> => {
    const prices: Record<string, CurrentPriceData> = {};

    // Start with polled data
    Object.entries(polledTickers).forEach(([symbol, ticker]) => {
      if (ticker.lastPrice && ticker.lastPrice > 0) {
        prices[symbol] = {
          price: ticker.lastPrice,
          source: 'polling',
          timestamp: ticker.timestamp || lastPollTime,
          ticker,
        };
      }
    });

    // Add API cached data for symbols not in polled data
    const allTickers = queryClient.getQueryData(['ticker']) as Ticker[] | Ticker;
    if (allTickers) {
      const tickerArray = Array.isArray(allTickers) ? allTickers : [allTickers];

      tickerArray.forEach(ticker => {
        if (ticker.symbol && ticker.lastPrice && ticker.lastPrice > 0) {
          // Only add if not already present from polling (polling has priority)
          if (!prices[ticker.symbol]) {
            prices[ticker.symbol] = {
              price: ticker.lastPrice,
              source: 'api',
              timestamp: ticker.timestamp || Date.now(),
              ticker,
            };
          }
        }
      });
    }

    return prices;
  }, [polledTickers, lastPollTime, queryClient]);

  return {
    getCurrentPrice,
    getAllPrices,
    isPollingActive: isPolling,
    lastUpdate: lastPollTime,
  };
}

// Utility hook for a single symbol's current price
export function useCurrentPrice(symbol: string): CurrentPriceData | null {
  const { getCurrentPrice } = useCurrentPrices();

  return useMemo(() => {
    return getCurrentPrice(symbol);
  }, [getCurrentPrice, symbol]);
}

// Hook for getting prices for multiple symbols
export function useCurrentPricesForSymbols(symbols: string[]): Record<string, CurrentPriceData | null> {
  const { getCurrentPrice } = useCurrentPrices();

  return useMemo(() => {
    const result: Record<string, CurrentPriceData | null> = {};
    symbols.forEach(symbol => {
      result[symbol] = getCurrentPrice(symbol);
    });
    return result;
  }, [getCurrentPrice, symbols]);
}
